import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Coffee, Clock, ClipboardList, Activity, ShieldCheck } from "lucide-react";

const DATASETS = [
  { key: "break-sessions", label: "Break Sessions", desc: "Every break with start, end, and duration per employee.", icon: Coffee, file: "break_sessions.csv" },
  { key: "working-hours", label: "Working Hours", desc: "Per member-day login, logout, worked, break, overtime, late.", icon: Clock, file: "working_hours.csv" },
  { key: "titles", label: "Titles", desc: "All dispatch titles with assignee, status, priority, and team.", icon: ClipboardList, file: "titles.csv" },
  { key: "activity", label: "Activity Log", desc: "Recent audit-trail activity across the platform.", icon: Activity, file: "activity.csv" },
  { key: "permission-changes", label: "Permission Changes", desc: "Role, permission, invite, and access changes.", icon: ShieldCheck, file: "permission_changes.csv" },
];

export default function ExportsCenter() {
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState("");

  const loadAudit = () =>
    api.get("/exports/audit").then(({ data }) => setAudit(data.items || [])).catch(() => {});

  useEffect(() => { loadAudit(); }, []);

  const download = async (dataset) => {
    setBusy(dataset.key);
    try {
      const res = await api.get(`/exports/${dataset.key}.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = dataset.file;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${dataset.label} exported`);
      loadAudit();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid="exports-center-page">
      <PageHeader title="Exports Center" subtitle="Every export is recorded in the audit trail" />
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {DATASETS.map((dataset) => (
          <Card key={dataset.key} className="p-5" data-testid={`export-card-${dataset.key}`}>
            <div className="flex items-center gap-2 text-[#255058]">
              <dataset.icon className="h-5 w-5" />
              <span className="font-display text-base font-semibold text-foreground">{dataset.label}</span>
            </div>
            <p className="mt-1 min-h-10 text-sm text-muted-foreground">{dataset.desc}</p>
            <Button
              onClick={() => download(dataset)}
              disabled={busy === dataset.key}
              data-testid={`export-btn-${dataset.key}`}
              className="mt-3 w-full gap-1.5 bg-[#255058] text-white hover:bg-[#1c3d43]"
            >
              <Download className="h-4 w-4" /> {busy === dataset.key ? "Exporting…" : "Export CSV"}
            </Button>
          </Card>
        ))}
      </div>
      <Card className="overflow-hidden" data-testid="export-audit-trail">
        <div className="border-b border-border px-5 py-4 font-display text-lg font-semibold">Export audit trail</div>
        <div className="divide-y divide-border">
          {audit.map((row) => (
            <div key={row.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
              <div>
                <span className="rounded-full bg-[#255058]/10 px-2 py-0.5 text-xs font-semibold text-[#255058]">
                  {row.actor_name}
                </span>{" "}
                <span className="uppercase text-muted-foreground">{row.dataset}</span>{" "}
                <span className="font-medium">{row.count} records</span>
              </div>
              <span className="shrink-0 text-xs text-muted-foreground">
                {row.created_at ? new Date(row.created_at).toLocaleString() : "—"}
              </span>
            </div>
          ))}
          {!audit.length && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">No exports yet.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
